(() => {
  if (window.__snugBundleVisualColourPickerLoaded) return;
  window.__snugBundleVisualColourPickerLoaded = true;

  const colourPattern = /colou?r/i;
  let openPicker = null;
  let placementFrame = 0;

  const positionPicker = () => {
    if (!openPicker?.isConnected) return;
    const trigger = openPicker.querySelector('[data-snug-colour-trigger]');
    const menu = openPicker.querySelector('[data-snug-colour-menu]');
    if (!trigger || !menu) return;
    const rect = trigger.getBoundingClientRect();
    const viewport = window.visualViewport;
    const viewportTop = viewport?.offsetTop || 0;
    const viewportBottom = viewportTop + (viewport?.height || window.innerHeight);
    const above = Math.max(0, rect.top - viewportTop - 12);
    const below = Math.max(0, viewportBottom - rect.bottom - 12);
    const opensUp = below < 160 && above > below;
    openPicker.classList.toggle('opens-up', opensUp);
    menu.style.maxHeight = `${Math.min(300, Math.max(44, opensUp ? above : below))}px`;
  };

  const schedulePlacement = () => {
    if (!openPicker || placementFrame) return;
    placementFrame = window.requestAnimationFrame(() => {
      placementFrame = 0;
      positionPicker();
    });
  };

  const closePicker = () => {
    if (!openPicker) return;
    const trigger = openPicker.querySelector('[data-snug-colour-trigger]');
    const menu = openPicker.querySelector('[data-snug-colour-menu]');
    openPicker.classList.remove('is-open');
    openPicker.classList.remove('opens-up');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (menu) menu.hidden = true;
    openPicker = null;
  };

  const setImage = (img, src, value) => {
    if (!src) {
      img.hidden = true;
      img.removeAttribute('src');
      return;
    }
    img.hidden = false;
    img.src = src;
    img.alt = `${value} sofa cover`;
    img.loading = 'lazy';
    img.decoding = 'async';
  };

  function initialise(root) {
    if (!root || root.dataset.snugVisualColourInitialised === 'true') return;

    const data = root.querySelector('[data-bundle-product]');
    const itemsContainer = root.querySelector('[data-bundle-items]');
    if (!data || !itemsContainer) return;

    let config;
    try {
      config = JSON.parse(data.textContent);
    } catch (error) {
      console.error('Snug visual colour picker could not read product data.', error);
      return;
    }

    const colourIndex = config.options.findIndex((option) => colourPattern.test(option.name || ''));
    if (colourIndex < 0) return;

    const imageByValue = new Map();
    config.variants.forEach((variant) => {
      const value = variant.options?.[colourIndex];
      if (!value || imageByValue.has(value) || !variant.image) return;
      imageByValue.set(value, variant.image);
    });

    const imageFor = (value) => imageByValue.get(value) || config.product.featuredImage || '';

    const focusOption = (wrap, direction) => {
      const options = [...wrap.querySelectorAll('[data-snug-colour-option]')].filter((option) => !option.disabled);
      if (!options.length) return;
      const active = document.activeElement;
      const current = options.indexOf(active);
      const nextIndex = current < 0
        ? (direction > 0 ? 0 : options.length - 1)
        : (current + direction + options.length) % options.length;
      options[nextIndex].focus();
    };

    const enhance = () => {
      itemsContainer.querySelectorAll('.snug-inline-bundle__select-wrap').forEach((original) => {
        if (original.dataset.snugVisualColour === 'true') return;

        const select = original.querySelector('select');
        const label = original.querySelector('span');
        if (!select || !label || !colourPattern.test(label.textContent || '')) return;

        const wrap = document.createElement('div');
        wrap.className = `${original.className} snug-inline-bundle__select-wrap--visual`;
        wrap.dataset.snugVisualColour = 'true';
        while (original.firstChild) wrap.appendChild(original.firstChild);
        original.replaceWith(wrap);

        select.classList.add('snug-colour-native');
        select.tabIndex = -1;
        select.setAttribute('aria-hidden', 'true');

        const trigger = document.createElement('button');
        trigger.type = 'button';
        trigger.className = 'snug-colour-picker__trigger';
        trigger.dataset.snugColourTrigger = 'true';
        trigger.setAttribute('aria-haspopup', 'listbox');
        trigger.setAttribute('aria-expanded', 'false');
        trigger.setAttribute('aria-label', `${select.getAttribute('aria-label') || label.textContent.trim()}: ${select.value}`);
        trigger.disabled = select.disabled;

        const triggerImage = document.createElement('img');
        triggerImage.className = 'snug-colour-picker__thumb';
        triggerImage.width = 38;
        triggerImage.height = 38;

        const triggerText = document.createElement('span');
        triggerText.className = 'snug-colour-picker__value';
        triggerText.textContent = select.value;

        const chevron = document.createElement('span');
        chevron.className = 'snug-colour-picker__chevron';
        chevron.setAttribute('aria-hidden', 'true');
        chevron.textContent = '⌄';

        setImage(triggerImage, imageFor(select.value), select.value);
        trigger.append(triggerImage, triggerText, chevron);

        const menu = document.createElement('div');
        menu.className = 'snug-colour-picker__menu';
        menu.id = `snug-colours-${root.id || config.product.id}-${select.dataset.itemIndex}-${select.dataset.optionIndex}`;
        menu.dataset.snugColourMenu = 'true';
        menu.setAttribute('role', 'listbox');
        menu.setAttribute('aria-label', label.textContent.trim());
        menu.hidden = true;
        trigger.setAttribute('aria-controls', menu.id);

        [...select.options].forEach((choice) => {
          const optionButton = document.createElement('button');
          optionButton.type = 'button';
          optionButton.className = 'snug-colour-picker__option';
          optionButton.dataset.snugColourOption = choice.value;
          optionButton.setAttribute('role', 'option');
          optionButton.setAttribute('aria-selected', String(choice.value === select.value));
          optionButton.disabled = choice.disabled;

          const optionImage = document.createElement('img');
          optionImage.className = 'snug-colour-picker__thumb';
          optionImage.width = 42;
          optionImage.height = 42;
          setImage(optionImage, imageFor(choice.value), choice.value);

          const optionName = document.createElement('span');
          optionName.className = 'snug-colour-picker__option-name';
          optionName.textContent = choice.textContent;

          const check = document.createElement('span');
          check.className = 'snug-colour-picker__check';
          check.setAttribute('aria-hidden', 'true');
          check.textContent = choice.value === select.value ? '✓' : '';

          optionButton.append(optionImage, optionName, check);
          optionButton.addEventListener('click', () => {
            if (choice.value === select.value) {
              closePicker();
              trigger.focus();
              return;
            }
            select.value = choice.value;
            closePicker();
            select.dispatchEvent(new Event('change', { bubbles: true }));
          });

          optionButton.addEventListener('keydown', (event) => {
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              focusOption(wrap, 1);
            } else if (event.key === 'ArrowUp') {
              event.preventDefault();
              focusOption(wrap, -1);
            } else if (event.key === 'Escape') {
              event.preventDefault();
              closePicker();
              trigger.focus();
            }
          });

          menu.appendChild(optionButton);
        });

        trigger.addEventListener('click', () => {
          const willOpen = !wrap.classList.contains('is-open');
          closePicker();
          if (!willOpen) return;
          wrap.classList.add('is-open');
          trigger.setAttribute('aria-expanded', 'true');
          menu.hidden = false;
          openPicker = wrap;
          positionPicker();
        });

        trigger.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            if (!wrap.classList.contains('is-open')) trigger.click();
            window.requestAnimationFrame(() => focusOption(wrap, event.key === 'ArrowDown' ? 1 : -1));
          } else if (event.key === 'Escape') {
            event.preventDefault();
            closePicker();
          }
        });

        wrap.addEventListener('focusout', (event) => {
          if (openPicker === wrap && !wrap.contains(event.relatedTarget)) closePicker();
        });

        wrap.append(trigger, menu);
      });
    };

    const observer = new MutationObserver(() => {
      if (openPicker && !openPicker.isConnected) openPicker = null;
      enhance();
    });
    observer.observe(itemsContainer, { childList: true, subtree: true });

    enhance();
    root.dataset.snugVisualColourInitialised = 'true';
  }

  document.addEventListener('click', (event) => {
    if (openPicker && !openPicker.contains(event.target)) closePicker();
  });
  document.addEventListener('scroll', schedulePlacement, { capture: true, passive: true });
  window.addEventListener('resize', schedulePlacement, { passive: true });
  window.visualViewport?.addEventListener('resize', schedulePlacement, { passive: true });
  window.visualViewport?.addEventListener('scroll', schedulePlacement, { passive: true });

  const initialiseAll = () => {
    document.querySelectorAll('[data-snug-inline-bundle]').forEach(initialise);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialiseAll, { once: true });
  } else {
    initialiseAll();
  }

  document.addEventListener('shopify:section:load', initialiseAll);
})();
